/**
 * Dashboard - Main view for Asset Performance
 * 
 * CRITICAL: This component is fully dynamic
 * - No hard-coded entity types
 * - No assumptions about data structure
 * - Renders based on what exists in SharePoint
 */

import React, { useEffect, useState, useMemo } from 'react';
import useStore from '../store/useStore';
import KPISummary from '../components/KPISummary';
import MetricsDisplay from '../components/MetricsDisplay';
import HSEMetricsDisplay from '../components/HSEMetricsDisplay';
import ProductionChart from '../components/ProductionChart';
import DynamicEntityTable from '../components/DynamicEntityTable';
import DependencyGraph from '../components/DependencyGraph';
import AlertsPanel from '../components/AlertsPanel';
import ExplanationPanel from '../components/ExplanationPanel';
import StaffPerformance from '../components/StaffPerformance';
import UserManagement from '../components/UserManagement';
import AddEntityModal from '../components/AddEntityModal';
import AddSignalModal from '../components/AddSignalModal';
import AddRuleModal from '../components/AddRuleModal';
import ExcelUpload from '../components/ExcelUpload';
import './Dashboard.css';

const Dashboard = () => {
  // Modal states
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  
  // View states
  const [activeView, setActiveView] = useState('dashboard');
  const [activeTab, setActiveTab] = useState('all');
  
  const {
    selectedAsset,
    entitiesByType,
    entitiesWithYTD,
    metricsSummary,
    targetMetrics,
    achievableMetrics,
    blockedAnalysis,
    dependencyGraph,
    explanations,
    unresolvedAlerts,
    staffPerformance,
    performanceSummary,
    refreshAllData,
    userRole,
    isLoading
  } = useStore();

  useEffect(() => {
    if (selectedAsset) {
      refreshAllData();
    }
  }, [selectedAsset, refreshAllData]);

  if (!selectedAsset) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading production intelligence system...</p>
      </div>
    );
  }

  const canEdit = userRole === 'AssetManager' || userRole === 'Admin';
  const isAdmin = userRole === 'Admin';

  // Filter HSE entities out of the dependency graph
  const filteredDependencyGraph = useMemo(() => {
    if (!dependencyGraph) return null;
    const hseTitles = new Set(
      (entitiesWithYTD || [])
        .filter(e => (e.Category || '').toLowerCase().includes('hse'))
        .map(e => e.Title)
    );
    if (hseTitles.size === 0) return dependencyGraph;
    return {
      nodes: dependencyGraph.nodes.filter(n => !hseTitles.has(n.title)),
      edges: dependencyGraph.edges.filter(e => !hseTitles.has(e.source) && !hseTitles.has(e.target)),
    };
  }, [dependencyGraph, entitiesWithYTD]);

  // Get entity types that exist for this asset, excluding Metric type
  const availableEntityTypes = Object.keys(entitiesByType).filter(t => t !== 'Metric');

  // If viewing User Management
  if (activeView === 'users') {
    return (
      <div className="dashboard">
        {/* Navigation Tabs */}
        <div className="dashboard-nav">
          <button 
            className="nav-tab"
            onClick={() => setActiveView('dashboard')}
          >
            📊 Dashboard
          </button>
          {isAdmin && (
            <button 
              className="nav-tab active"
              onClick={() => setActiveView('users')}
            >
              👥 User Management
            </button>
          )}
        </div>

        {/* User Management Component */}
        <UserManagement />
      </div>
    );
  }

  // Main Dashboard View
  return (
    <div className="dashboard">
      {/* Navigation Tabs */}
      <div className="dashboard-nav">
        <button 
          className="nav-tab active"
          onClick={() => setActiveView('dashboard')}
        >
          📊 Dashboard
        </button>
        {isAdmin && (
          <button 
            className="nav-tab"
            onClick={() => setActiveView('users')}
          >
            👥 User Management
          </button>
        )}
      </div>

      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h2>{selectedAsset.Title} - Asset Performance</h2>
          <p className="asset-code">{selectedAsset.AssetCode || 'N/A'}</p>
          <p className="last-updated">Last updated: {new Date().toLocaleString()}</p>
        </div>
        
        {canEdit && (
          <div className="dashboard-actions">
            <button className="btn-primary" onClick={() => setShowEntityModal(true)}>
              + Add Entity
            </button>
            <button className="btn-secondary" onClick={() => setShowSignalModal(true)}>
              📊 Update Signal
            </button>
            <button className="btn-secondary" onClick={() => setShowRuleModal(true)}>
              🔗 Add Rule
            </button>
            <button className="btn-excel" onClick={() => setShowExcelUpload(true)}>
              📤 Excel Upload
            </button>
           
          </div>
        )}
      </div>

      {/* Alerts Banner */}
      {unresolvedAlerts && unresolvedAlerts.length > 0 && (
        <div className="alerts-banner">
          <span className="alert-icon">⚠️</span>
          <span>{unresolvedAlerts.length} unresolved alert{unresolvedAlerts.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Production Metrics - YTD Performance */}
      {entitiesWithYTD && entitiesWithYTD.length > 0 && (
        <section className="dashboard-section">
          <MetricsDisplay entitiesWithYTD={entitiesWithYTD} />
        </section>
      )}

      {/* HSE Metrics - Current State */}
      {entitiesWithYTD && entitiesWithYTD.length > 0 && (
        <section className="dashboard-section">
          <HSEMetricsDisplay entitiesWithYTD={entitiesWithYTD} />
        </section>
      )}

      {/* Production Trend Chart */}
      {entitiesWithYTD && entitiesWithYTD.length > 0 && (
        <section className="dashboard-section">
          <ProductionChart />
        </section>
      )}

      {/* KPI Summary */}
      {/* <section className="dashboard-section">
        <h3>Asset Performance Overview</h3>
        <KPISummary
          targetMetrics={targetMetrics}
          achievableMetrics={achievableMetrics}
          blockedAnalysis={blockedAnalysis}
          metricsSummary={metricsSummary}
        />
      </section> */}

      {/* Explanation Panel */}
      {explanations && (
        <section className="dashboard-section">
          <h3>Intelligent Analysis</h3>
          <ExplanationPanel explanations={explanations} />
        </section>
      )}

      {/* Dependency Graph */}
      {filteredDependencyGraph && filteredDependencyGraph.nodes && filteredDependencyGraph.nodes.length > 0 && (
        <section className="dashboard-section">
          <h3>Dependency Network</h3>
          <DependencyGraph graph={filteredDependencyGraph} />
        </section>
      )}

      {/* Dynamic Entity Tables */}
      <section className="dashboard-section">
        <div className="section-header">
          <h3>Entities</h3>
          <div className="entity-type-tabs">
            <button 
              className={`tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All ({availableEntityTypes.reduce((sum, t) => sum + (entitiesByType[t]?.length || 0), 0)})
            </button>
            {availableEntityTypes.map(type => (
              <button
                key={type}
                className={`tab ${activeTab === type ? 'active' : ''}`}
                onClick={() => setActiveTab(type)}
              >
                {type} ({entitiesByType[type]?.length || 0})
              </button>
            ))}
          </div>
        </div>
        
        {activeTab === 'all' ? (
          <div className="all-entities-view">
            {availableEntityTypes.map(type => (
              <div key={type} className="entity-type-section">
                <h4>{type}s</h4>
                <DynamicEntityTable 
                  entities={entitiesByType[type]} 
                  entityType={type}
                  canEdit={canEdit}
                />
              </div>
            ))}
            
            {availableEntityTypes.length === 0 && (
              <div className="empty-state">
                <p>No entities defined for this asset.</p>
                <p>Click "Add Entity" to create your first one.</p>
              </div>
            )}
          </div>
        ) : (
          <DynamicEntityTable 
            entities={entitiesByType[activeTab] || []} 
            entityType={activeTab}
            canEdit={canEdit}
          />
        )}
      </section>

      {/* Staff Performance */}
      {staffPerformance && staffPerformance.length > 0 && (
        <section className="dashboard-section">
          <h3>Staff Performance Scorecard</h3>
          <StaffPerformance 
            staffPerformance={staffPerformance}
            performanceSummary={performanceSummary}
          />
        </section>
      )}

      {/* Alerts Panel */}
      {/* <section className="dashboard-section">
        <h3>Alerts & Notifications</h3>
        <AlertsPanel alerts={unresolvedAlerts} canEdit={canEdit} />
      </section> */}

      {/* Modals */}
      {showEntityModal && (
        <AddEntityModal onClose={() => setShowEntityModal(false)} />
      )}
      
      {showSignalModal && (
        <AddSignalModal onClose={() => setShowSignalModal(false)} />
      )}
      
      {showRuleModal && (
        <AddRuleModal onClose={() => setShowRuleModal(false)} />
      )}

      {showExcelUpload && (
        <ExcelUpload onClose={() => setShowExcelUpload(false)} />
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Evaluating production intelligence...</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;